'use client';

import { useState, useCallback, useMemo, useRef } from 'react';
import ImageWrapper from '@/helpers/Wrappers/ImageWrapper/ImageWrapper';
import { toEnhancedImage } from '@/utils/image-utils';
import { Container } from '@/components/primitives/Container/Container';
import { IBaseComponentProps, WithMetadata } from '@/lib/types';
import { ITimeline, IFile, CSLPFieldMapping } from '@/.generated';
import { timelineVariants } from './Timeline.styles';
import { getCSLPAttributes } from '@/utils/type-guards';
import RichTextWrapper from '@/helpers/Wrappers/RichTextWrapper/RichTextWrapper';
import { useGlobalLabels } from '@/context/GlobalLabelContext';
import { trackAdobeClick } from '@/utils/adobe/click';

type TimelineProps = WithMetadata<ITimeline> & IBaseComponentProps;

type TimelineItem = NonNullable<TimelineProps['timeline_items']>[number];
type MarkerItem = NonNullable<
  NonNullable<TimelineProps['timeline_markers']>['marker_item']
>[number];

// Renders the timeline image through the shared wrapper, which serves resized WebP straight from the CDN
const TimelineImage = ({
  image,
  alt,
  className,
  cslpAttribute,
}: {
  image?: IFile | null;
  alt: string;
  className: string;
  cslpAttribute?: CSLPFieldMapping;
}) => {
  if (!image?.url) return null;

  return (
    <ImageWrapper
      image={toEnhancedImage(image, {
        alternateText: alt,
        fallbackWidth: 360,
        fallbackHeight: 240,
        cslp: cslpAttribute,
      })}
      imageClassName={className}
    />
  );
};

// Renders a single timeline item with number, progress line, title, description and image
const TimelineItemComponent = ({
  item,
  index,
  styles,
  onBackToTop,
  backToTopLabel,
}: {
  item: TimelineItem;
  index: number;
  styles: ReturnType<typeof timelineVariants>;
  onBackToTop: () => void;
  backToTopLabel: string;
}) => {
  return (
    <div className={styles.timelineItem()} id={item.marker_point_id || `timeline-item-${index}`}>
      <div className={styles.imageWrapper()}>
        <TimelineImage
          image={item.image}
          alt={item.title || 'Timeline Item Image'}
          className={styles.image()}
          cslpAttribute={item.$?.image}
        />
      </div>
      <div className={styles.contentWrapper()}>
        {item.title && (
          <p className={styles.title()} {...getCSLPAttributes(item.$?.title)}>
            {item.title}
          </p>
        )}
        <div className={styles.horizontalLine()}>
          <span className={styles.horizontalLineDot()} aria-hidden="true" />
        </div>
        {item.description && (
          <RichTextWrapper
            content={item.description}
            className={styles.description()}
            {...getCSLPAttributes(item.$?.description)}
          />
        )}
      </div>
      {item.enable_back_to_top_anchor && (
        <div className={styles.backToTopLinkWrapper()}>
          <button
            type="button"
            className={styles.backToTopLink()}
            onClick={onBackToTop}
            aria-label="Back to top of timeline"
          >
            {backToTopLabel.toUpperCase()}
          </button>
        </div>
      )}
    </div>
  );
};

export const Timeline = (props: TimelineProps) => {
  const {
    componentName,
    _metadata,
    timeline_markers,
    timeline_items,
    hide_component,
    inline_navigation_id,
    $,
  } = props;
  const styles = timelineVariants();

  const [selectedMarkerId, setSelectedMarkerId] = useState<string>('');
  const {
    globalLabels: { back_to_top_label = 'Back To Top' },
  } = useGlobalLabels();

  // Filter markers that have valid IDs
  const validMarkers = useMemo(
    () => timeline_markers?.marker_item?.filter((marker) => marker.id) || [],
    [timeline_markers?.marker_item]
  );

  // Component identifier shared by the dropdown Click events
  const clickLocation = `${componentName}_${_metadata?.uid}`;
  // Dedupes the open Click: a native select fires mousedown more than once per open gesture
  const openTrackedRef = useRef(false);

  // Track opening the marker dropdown once per open cycle (mousedown can fire repeatedly)
  const handleMarkerOpen = useCallback(() => {
    if (openTrackedRef.current) return;
    openTrackedRef.current = true;
    trackAdobeClick({ clickName: 'Select timeline step', clickType: 'dropdown', clickLocation });
  }, [clickLocation]);

  // Reset the open guard so the next open is tracked again
  const handleMarkerBlur = useCallback(() => {
    openTrackedRef.current = false;
  }, []);

  // Handles dropdown selection and scrolls to first matching item
  const handleMarkerChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const markerId = event.target.value;
      openTrackedRef.current = false;
      trackAdobeClick({
        clickName: event.target.selectedOptions[0]?.text || 'Select timeline step',
        clickType: 'dropdown-option',
        clickLocation,
      });
      setSelectedMarkerId(markerId);

      if (markerId) {
        const targetItem = timeline_items?.find((item) => item.marker_point_id === markerId);
        if (targetItem?.marker_point_id) {
          const element = document.getElementById(targetItem.marker_point_id);
          element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    },
    [timeline_items, clickLocation]
  );

  // Scrolls back to the dropdown selector
  const handleBackToTop = useCallback(() => {
    trackAdobeClick({
      clickName: back_to_top_label,
      clickType: 'button',
      clickLocation: 'timeline',
    });
    const dropdownElement = document.querySelector('[data-timeline-dropdown]');
    dropdownElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [back_to_top_label]);

  if (hide_component) return null;

  if (!timeline_items?.length) return null;

  return (
    <Container componentName={componentName} metaId={_metadata?.uid} id={inline_navigation_id}>
      <div className={styles.wrapper()}>
        {validMarkers.length > 0 && (
          <div className={styles.dropdownWrapper()}>
            <select
              className={styles.dropdown()}
              value={selectedMarkerId}
              onMouseDown={handleMarkerOpen}
              onChange={handleMarkerChange}
              onBlur={handleMarkerBlur}
              aria-label="Select timeline step"
              data-timeline-dropdown
              {...getCSLPAttributes($?.timeline_markers)}
            >
              {validMarkers.map((marker: MarkerItem, index: number) => (
                <option key={marker.id || index} value={marker.id || ''}>
                  {marker.title}
                </option>
              ))}
            </select>
            <i className={styles.dropdownIcon()} />
          </div>
        )}
        <div className={styles.timelineContainer()} {...getCSLPAttributes($?.timeline_items)}>
          {timeline_items.map((item, index) => (
            <TimelineItemComponent
              key={item.marker_point_id || index}
              item={item}
              index={index}
              styles={styles}
              onBackToTop={handleBackToTop}
              backToTopLabel={back_to_top_label}
            />
          ))}
        </div>
      </div>
    </Container>
  );
};
